import { AppContext } from 'App'
import brand from 'brand'
import Alert from 'components/alert'
import AppButton from 'components/app-button'
import ErrorPage from 'components/error-page'
import FormattedDate from 'components/formatted-date'
import {
  BoxIcon,
  ChatBubbleIcon,
  CreditCardIcon,
  DropOffIcon,
  EllipsisIcon,
  LeafIcon,
  PaperClipIcon,
  PrinterIcon,
  PuzzlePieceIcon,
} from 'components/icons'
import SlideInTransition from 'components/slide-in-transition'
import SummaryEditForm from 'components/summary-edit-form'
import TwoColumnLayout from 'components/two-column-layout'
import useForm from 'hooks/use-form'
import { __ } from 'lang'
import { upperFirst } from 'lodash'
import { createContext, useContext, useMemo, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import PaymentsService from 'services/payments-service'
import ReturnsService from 'services/returns-service'
import { pluralize, useMount } from 'utils'

const PageContext = createContext({
  order: null,
  orderReturn: null,
  carriers: null,
})

const ReturnLineItemModel = (attributes) => ({
  ...attributes,
  images: JSON.parse(attributes.images),
})

export default function Page() {
  const [order, setOrder] = useState(null)
  const [error, setError] = useState(null)
  const [orderReturn, setOrderReturn] = useState(null)
  const [carriers, setCarriers] = useState(null)

  const app = useContext(AppContext)
  const navigate = useNavigate()

  useMount(() => {
    async function fetchOrderDetails() {
      try {
        const fetchedOrder = (await ReturnsService.getOrder({ hash: app.token })).data.data

        if (fetchedOrder.isCreateEnabled) {
          navigate('/request', { replace: true })
          return
        }

        const fetchedReturn = fetchedOrder.returns.filter((r) => r.status.toLowerCase() === 'open')[0]
        const fetchedCarriers = (await ReturnsService.getCarriers(fetchedReturn.return_hash)).data

        setOrder(fetchedOrder)
        setOrderReturn(fetchedReturn)
        setCarriers(fetchedCarriers)
      } catch (err) {
        if (err.response?.data?.code === 'ERR_SESSION_TIMEOUT') {
          navigate('/?status=ERR_SESSION_TIMEOUT')
        } else {
          setError(err.response)
        }
      }
    }

    fetchOrderDetails()
  })

  return (
    <PageContext.Provider
      value={{
        order,
        orderReturn,
        carriers,
      }}
    >
      <TwoColumnLayout page="summary">
        <div className="mx-auto w-full max-w-xl px-4 py-12 md:px-6">
          {order && error === null && <PageLoaded />}
          {order === null && error === null && <PageLoading />}
          {error !== null && <PageError />}
        </div>
      </TwoColumnLayout>
    </PageContext.Provider>
  )
}

function PageLoaded() {
  const [status, setStatus] = useState()
  const [expandedItems, setExpandedItems] = useState([])
  const [searchParams] = useSearchParams()

  const page = useContext(PageContext)
  const navigate = useNavigate()

  const hasFaultyItem = useMemo(
    () =>
      page.orderReturn.line_items.filter((li) =>
        ['FAULT', 'FAULTY'].includes(String(li.reason_to_return).toUpperCase()),
      ).length,
    [page.orderReturn],
  )

  const canPurchaseShippingLabel = useMemo(() => {
    if (page?.carriers.length === 0) return false

    const address = page.order?.data?.sales_order_addresses?.find((address) => address.is_shipping)
    if (!address) return false

    const country = address.country_code.toLowerCase()
    const state = address.state.toLowerCase()

    if (country === 'us') {
      return !['hawaii', 'alaska'].includes(state)
    } else {
      return true
    }
  }, [page.order, page.carriers])

  useMount(() => {
    if (searchParams.has('status')) {
      setStatus(searchParams.get('status'))
      navigate('/summary', { replace: true })
    }
  })

  function toggleAttachments(item) {
    setExpandedItems((prevValue) =>
      prevValue.includes(item.id)
        ? prevValue.filter((i) => i.id === item.id) //
        : [...prevValue, item.id],
    )
  }

  return (
    <div>
      <Link to="/" className="text-xs uppercase tracking-widest">
        &times; Exit
      </Link>

      <h1 className="mt-6 text-lg uppercase tracking-widest">{__('page_summary_title')}</h1>

      <div
        className="mt-4 text-sm leading-relaxed text-gray-500"
        dangerouslySetInnerHTML={{ __html: __('page_summary_message') }}
      />

      {status === 'payment_success' && (
        <Alert
          className="mt-10"
          title={__('payment_success_title')}
          description={__('payment_success_description')}
          variant="success"
        />
      )}

      {status === 'payment_cancel' && (
        <Alert
          className="mt-10"
          title={__('payment_cancel_title')}
          description={__('payment_cancel_description')}
          variant="warning"
        />
      )}

      {status === 'payment_error' && (
        <Alert
          className="mt-10"
          title={__('payment_error_title')}
          description={__('payment_error_description')}
          variant="danger"
        />
      )}

      <div className="mt-10 flex items-baseline justify-between">
        <div>
          <h6 className="text-sm uppercase tracking-widest text-gray-700">Reference Number</h6>
          <h2 className="mt-2 text-sm uppercase text-primary-700">{page.orderReturn.return_hash}</h2>
        </div>
        <div>
          <SummaryEditForm page={page} />
        </div>
      </div>

      <hr className="mt-6" />

      <ul>
        {page.orderReturn.line_items.map(ReturnLineItemModel).map((item) => (
          <li key={item.id} className="flex flex-wrap items-start border-b py-8">
            <div className="w-20 sm:w-24">
              <div className="aspect-[1/1.5] overflow-hidden rounded-md ring-1 ring-black/5">
                <img
                  className="h-full w-full object-cover"
                  src={item.order_line_item?.image_url}
                  alt={item.order_line_item?.name}
                />
              </div>
            </div>

            <div className="ml-6 flex-1">
              <h1 className="text-sm font-medium">{item.order_line_item?.name}</h1>
              <h6 className="text-sm text-gray-700">{item.order_line_item?.sku}</h6>

              <div className="mt-4 flex items-start">
                <ChatBubbleIcon className="mr-4 h-5 w-5 text-gray-400" />
                <span className="text-sm text-gray-700">Reason: &quot;{item.reason_to_return}&quot;</span>
              </div>

              <div className="mt-4 flex items-start">
                <EllipsisIcon className="mr-4 h-5 w-5 text-gray-400" />
                <span className="text-sm text-gray-700">
                  {pluralize(item.qty_to_return, __(':value item to return'), __(':value items to return'))}
                </span>
              </div>

              {item.order_line_item.piece_quantity > 1 && (
                <div className="mt-4 flex items-start">
                  <PuzzlePieceIcon className="mr-4 h-5 w-5 text-gray-400" />
                  <span className="text-sm text-gray-700">
                    <span>{`${item.order_line_item.piece_quantity}-piece set`}</span>
                  </span>
                </div>
              )}

              {item.images?.length > 0 && (
                <div className="mt-4 flex items-start text-gray-700">
                  <PaperClipIcon className="mr-4 h-5 w-5 text-gray-400" />
                  <button type="button" onClick={() => toggleAttachments(item)} className="text-sm underline">
                    {expandedItems.includes(item.id)
                      ? pluralize(item.images.length, __('Hide :value attachment'), __('Hide :value attachments'))
                      : pluralize(item.images.length, __('Show :value attachment'), __('Show :value attachments'))}
                  </button>
                </div>
              )}

              <SlideInTransition show={expandedItems.includes(item.id)}>
                <div className="mt-6 flex flex-wrap space-x-4">
                  {item.images?.map((image) => (
                    <a
                      className="group aspect-square w-12 overflow-hidden rounded border"
                      href={image}
                      key={image}
                      rel="referrer noopener noreferrer"
                      target="_blank"
                    >
                      <img src={image} className="h-full w-full object-scale-down transition group-hover:scale-150" />
                    </a>
                  ))}
                </div>
              </SlideInTransition>
            </div>
          </li>
        ))}
      </ul>

      <div className="mt-8 space-y-4">
        <div className="flex items-start justify-between">
          <div className="text-sm text-gray-500">{__('Reference number')}</div>
          <div className="text-sm uppercase text-gray-700">{page.orderReturn.return_hash}</div>
        </div>
        <div className="flex items-start justify-between">
          <div className="text-sm text-gray-500">{__('Requested on')}</div>
          <FormattedDate className="text-sm text-gray-700" value={page.orderReturn.created_at} />
        </div>
        <div className="flex items-start justify-between">
          <div className="text-sm text-gray-500">{__('Order number')}</div>
          <div className="text-sm uppercase text-gray-700">
            {String(page.orderReturn.order_no).replaceAll(',', ' + ')}
          </div>
        </div>
        <div className="flex items-start justify-between">
          <div className="text-sm text-gray-500">{__('Status')}</div>
          <div className="text-sm text-gray-700">{upperFirst(page.orderReturn.status)}</div>
        </div>
        <div className="flex items-start justify-between">
          <div className="text-sm text-gray-500">{__('Refund using')}</div>
          <div className="text-sm text-gray-700">
            {page.orderReturn.is_use_store_credit && 'Store Credits'}
            {page.orderReturn.is_use_refund && 'Refund'}
          </div>
        </div>
      </div>

      <hr className="mt-8" />

      {canPurchaseShippingLabel ? (
        hasFaultyItem && !page.orderReturn.shipment_id ? (
          <div className="mt-8">
            <h6 className="text-sm uppercase tracking-widest text-gray-700">{__("What's next?")}</h6>
            <p className="mt-6 text-sm leading-relaxed text-gray-700">{__('shipping_label_instructions_free')}</p>
          </div>
        ) : (
          <div className="mt-8">
            <h6 className="text-sm uppercase tracking-widest text-gray-700">{__("What's next?")}</h6>
            <div className="mt-6">{page.orderReturn.shipment_id ? <PrintLabel /> : <Checkout />}</div>
          </div>
        )
      ) : (
        <div className="mt-8">
          <h6 className="text-sm uppercase tracking-widest text-gray-700">{__("What's next?")}</h6>
          <p className="mt-6 text-sm leading-relaxed text-gray-500">
            {__('shipping_label_instructions_rest_of_the_world')}
          </p>
          <div className="mt-6">
            <PrintForm />
          </div>
          <div className="mt-4 bg-gray-100 p-6">
            <div className="text-sm text-gray-500">Package the PDF with your return and send it to:</div>
            <div
              className="mt-4 text-sm text-gray-700"
              dangerouslySetInnerHTML={{
                __html: __(page.orderReturn.store_id === 1 ? 'address_au' : 'address_us'),
              }}
            />
          </div>
        </div>
      )}
    </div>
  )
}

function PageLoading() {
  return (
    <div>
      <div className="h-5 w-64 animate-pulse bg-gray-100"></div>
      <div className="mt-2 h-5 w-40 animate-pulse bg-gray-200"></div>
      <div className="mt-10 h-5 w-2/3 animate-pulse bg-gray-100"></div>
      <div className="mt-2 h-5 w-full animate-pulse bg-gray-200"></div>
      <div className="mt-2 h-5 w-1/3 animate-pulse bg-gray-100"></div>
    </div>
  )
}

function PageError() {
  return (
    <ErrorPage>
      <p className="text-sm leading-relaxed text-gray-700">
        {__('We are unable to get your return information this time.')}
      </p>
      <Link to="/" className="mt-6 inline-block border-b border-gray-400 py-1 text-sm text-gray-700">
        {__('Return to home')}
      </Link>
    </ErrorPage>
  )
}

function PrintLabel() {
  const page = useContext(PageContext)
  const [shipment, setShipment] = useState(null)

  const shippingCityAndState = useMemo(() => {
    const soa = page.order?.data?.sales_order_addresses?.find((address) => address.is_shipping)
    return soa && [soa.city, soa.state].filter(Boolean).join(', ')
  }, [page.order])

  const form = useForm({
    request: ReturnsService.getShipment,

    defaults: {
      hash: page.orderReturn.return_hash,
    },

    onSuccess: (res) => {
      setShipment(res.data.data)
    },
  })

  useMount(() => {
    form.submit()
  })

  if (form.processing) {
    return (
      <div className="space-y-4">
        <div className="mt-2 h-5 w-40 animate-pulse bg-gray-200"></div>
        <div className="mt-10 h-5 w-2/3 animate-pulse bg-gray-100"></div>
        <div className="mt-2 h-5 w-full animate-pulse bg-gray-200"></div>
      </div>
    )
  }

  if (form.error) {
    return (
      <Alert
        description="An error occurred while retrieving your shipping label."
        title="Shipping label is unavailable."
        variant="danger"
        tabIndex="-1"
      />
    )
  }

  const labelUrl = shipment?.label_url

  if (shipment?.status === 'QR-Code') {
    return (
      <div className="border p-6">
        <h1 className="text-center font-semibold">Your Returns Confirmation</h1>
        <p className="mt-6 text-sm leading-relaxed text-gray-700">
          Your return <span className="font-semibold">{shipment?.tracking_code}</span> has been started. <br />
          We&apos;ve sent a confirmation email to {page.order.email}
        </p>
        <img className="mx-auto mt-6 h-auto w-24" src={labelUrl} />
        <h2 className="mt-6 font-medium">Next Steps</h2>
        <p className="mt-2 text-sm leading-relaxed text-gray-700">
          Drop off items at any Return Bar® location. No packaging or label needed.
        </p>
        <AppButton
          as="a"
          className="relative mt-6 w-full px-5 py-3 text-xs uppercase tracking-widest"
          color="primary"
          href={`https://locations.happyreturns.com/?retailer=${brand.happyreturns.retailer}&distance=50&address=${shippingCityAndState}`}
          rel="noopener noreferrer"
          target="_blank"
        >
          See all Return Bar® Locations
        </AppButton>
      </div>
    )
  }

  return (
    <div className="mt-6 space-y-4">
      <div
        className="space-y-6 text-sm leading-relaxed text-gray-700"
        dangerouslySetInnerHTML={{ __html: __('shipping_label_instructions') }}
      />
      <AppButton
        className="relative w-full px-5 py-3 text-xs uppercase tracking-widest"
        color="primary"
        target="_blank"
        rel="noreferrer noopener"
        href={labelUrl}
        as="a"
      >
        <PrinterIcon className="absolute left-3 h-6 w-6 text-gray-200" />
        <span>Print shipping label</span>
      </AppButton>
    </div>
  )
}

function PrintForm() {
  const page = useContext(PageContext)

  const form = useForm({
    request: ReturnsService.getPdf,

    defaults: {
      hash: page.orderReturn.return_hash,
    },

    onSuccess: (res) => {
      window.open(res.data.url, '_blank', 'noreferrer,noopener')
    },
  })

  return (
    <div className="space-y-4">
      <AppButton
        className="relative w-full px-5 py-3 text-xs uppercase tracking-widest"
        onClick={() => form.submit()}
        color="primary"
        type="button"
      >
        <PrinterIcon className="absolute left-3 h-6 w-6 text-gray-200" />
        <span>Print return request form</span>
      </AppButton>
    </div>
  )
}

function Checkout() {
  const page = useContext(PageContext)

  const allStandardCarriers = page.carriers?.filter((c) => c.type === 'standard') || []
  const standardCarrier = allStandardCarriers.find((c) => c.allowed) || allStandardCarriers[0]

  const allDropoffCarriers = page.carriers?.filter((c) => c.type === 'dropoff') || []
  const dropoffCarrier = allDropoffCarriers.find((c) => c.allowed) || allDropoffCarriers[0]

  return (
    <div className="space-y-4">
      {allDropoffCarriers.length > 0 &&
        (dropoffCarrier?.code === 'us-happy-returns' ? (
          <HappyReturnsWidget carrier={dropoffCarrier} />
        ) : (
          <DropoffReturnsWidget carrier={dropoffCarrier} />
        ))}
      {allStandardCarriers.length > 0 && <StandardReturnsWidget carrier={standardCarrier} />}
      <p className="bg-gray-100 p-6 text-sm text-gray-500">{__('payment_tooltip')}</p>
    </div>
  )
}

function HappyReturnsWidget({ carrier }) {
  const page = useContext(PageContext)

  const form = useForm({
    request: PaymentsService.createCheckout,

    defaults: {
      return_hash: page.orderReturn.return_hash,
      return_method: carrier?.code,
    },

    onSuccess: (res) => {
      window.location.href = res.data.url
    },
  })

  const itemsQty = page.orderReturn.line_items.reduce((sum, li) => sum + li.qty_to_return, 0)

  return (
    <div className="border p-4 transition duration-500 hover:border-black">
      <div className="flex flex-col items-start gap-6 md:flex-row">
        <img className="h-10 w-10 object-cover md:h-14 md:w-14" src="/returns/images/happy-returns-logo.svg" />
        <div className="flex-1">
          <h6 className="text-sm text-gray-500">
            <LeafIcon className="mr-1 inline h-4 w-4 text-green-500" />
            <span>Most sustainable option</span>
          </h6>
          <h1 className="mt-1 text-lg text-gray-900">Drop Off Return</h1>
          <p className="mt-1 text-sm text-gray-600">Packaging & label-free. Refund initiated immediately.</p>
        </div>
      </div>
      <hr className="my-4" />
      <div className="flex items-center justify-between">
        <div className="hidden text-sm text-gray-500 sm:block">Return Bar® Locations near you</div>
        <div className="text-sm text-gray-600">
          <a target="_blank" rel="noopener noreferrer" href={carrier?.location_url || '#'} className="underline">
            See all Return Bar® Locations
          </a>
        </div>
      </div>
      {itemsQty > 9 && <p className="mt-2 text-xs text-gray-500">* Must be 9 items or less</p>}
      {carrier?.allowed ? (
        <AppButton
          className="relative mt-4 w-full px-5 py-3 text-xs uppercase tracking-widest"
          color="yellow"
          disabled={form.processing}
          onClick={() => form.submit()}
          type="button"
        >
          <CreditCardIcon className="absolute left-3 h-6 w-6 text-black" />
          <span>Return via Happy Returns</span>
        </AppButton>
      ) : (
        <div className="px-5 py-3 bg-gray-100 text-gray-700 text-sm mt-4 text-center">
          <span>Sorry, this service is currently unavailable.</span>
        </div>
      )}
    </div>
  )
}

function StandardReturnsWidget({ carrier }) {
  const page = useContext(PageContext)

  const form = useForm({
    request: PaymentsService.createCheckout,

    defaults: {
      return_hash: page.orderReturn.return_hash,
      return_method: carrier?.code,
    },

    onSuccess: (res) => {
      window.location.href = res.data.url
    },
  })

  return (
    <div className="border p-4 transition duration-500 hover:border-black">
      <div className="flex flex-col items-start gap-6 md:flex-row">
        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gray-100 text-gray-700 md:h-14 md:w-14">
          <BoxIcon className="h-6 w-6" />
        </div>
        <div className="flex-1">
          <h1>Standard Post</h1>
          <p className="mt-1 text-sm leading-relaxed text-gray-700">{__('shipping_label_instructions')}</p>
        </div>
      </div>
      {carrier?.allowed ? (
        <AppButton
          className="relative mt-4 w-full px-5 py-3 text-xs uppercase tracking-widest"
          color="primary"
          disabled={form.processing}
          onClick={() => form.submit()}
          type="button"
        >
          <CreditCardIcon className="absolute left-3 h-6 w-6 text-gray-100" />
          <span>Purchase a shipping label</span>
        </AppButton>
      ) : (
        <div className="px-5 py-3 bg-gray-100 text-gray-700 text-sm mt-4 text-center">
          Sorry, this service is currently unavailable.
        </div>
      )}
    </div>
  )
}

function DropoffReturnsWidget({ carrier }) {
  const page = useContext(PageContext)

  const form = useForm({
    request: PaymentsService.createCheckout,

    defaults: {
      return_hash: page.orderReturn.return_hash,
      return_method: carrier?.code,
    },

    onSuccess: (res) => {
      window.location.href = res.data.url
    },
  })

  return (
    <div className="border p-4 transition duration-500 hover:border-black">
      <div className="flex flex-col items-start gap-6 md:flex-row">
        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gray-100 text-gray-700 md:h-14 md:w-14">
          <DropOffIcon className="h-6 w-6" />
        </div>
        <div className="flex-1">
          <h1 className="mt-1 text-lg text-gray-900">Drop Off Return</h1>
          <p className="mt-1 text-sm text-gray-600">Packaging & label-free. Refund initiated immediately.</p>
        </div>
      </div>
      <hr className="my-4" />
      <div className="flex items-center justify-between">
        <div className="hidden text-sm text-gray-500 sm:block">Drop off locations near you</div>
        <div className="text-sm text-gray-600">
          <a target="_blank" rel="noopener noreferrer" href={carrier?.location_url || '#'} className="underline">
            See all drop off locations
          </a>
        </div>
      </div>
      {carrier?.allowed ? (
        <AppButton
          className="relative mt-4 w-full px-5 py-3 text-xs uppercase tracking-widest"
          color="primary"
          disabled={form.processing}
          onClick={() => form.submit()}
          type="button"
        >
          <CreditCardIcon className="absolute left-3 h-6 w-6 text-gray-100" />
          <span>Purchase a QR code</span>
        </AppButton>
      ) : (
        <div className="px-5 py-3 bg-gray-100 text-gray-700 text-sm mt-4 text-center">
          <span>Sorry, this service is currently unavailable.</span>
        </div>
      )}
    </div>
  )
}

